import { useCallback, useMemo, useState } from 'react';
import Badge from '@mui/material/Badge';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Iconify from '../../../components/Iconify';
import IconButton from '../../../components/IconButton';
import { ListItemText, Menu, MenuItem } from '../../../components';
import { colors } from '../../../theme/palette/darkPalette';
import { handleUrlDownload } from '../../../utils/download.util';
import DownloadsService from '../../../services/downloads.service';
import useUpdateDownloadResources from 'src/queries/downloads/useUpdateDownloadResources';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DOWNLOADS_QUERY_KEY } from 'src/constants/query';
import { useQueryClient } from 'react-query';
import useFetchDownloads from 'src/queries/downloads/useFetchDownloads';
import { useRecoilState } from 'recoil';
import { notificationState } from 'src/state/main';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';

const NotificationBar = () => {
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const [unreadNotification, setUnreadNotification] = useRecoilState(notificationState);
  const downloadResourcesQuery = useFetchDownloads({ customerEnvId });

  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState(null);

  const { mutate: updateResourcesStatus } = useUpdateDownloadResources({ customerEnvId });

  const updateResourceStatus = useCallback(
    ({ downloadId, resources }) => {
      if (resources.length === 1 && resources[0].seen) return;
      const selectedResource = resources.find(resource => resource.id === downloadId);
      if (selectedResource.seen) return;

      DownloadsService.updateDownloadResourceStatus({ downloadId }).then(updatedResource => {
        queryClient.setQueryData([DOWNLOADS_QUERY_KEY, customerEnvId], prevData => {
          if (!prevData || !prevData.pages) return prevData;

          const newData = {
            ...prevData,
            pages: prevData.pages.map(page => {
              const updatedDownloads = page.downloads.map(download => {
                if (download.id === updatedResource.id) {
                  return { ...download, ...updatedResource };
                }
                return download;
              });
              return { ...page, downloads: updatedDownloads };
            }),
          };

          return newData;
        });
      });
    },
    [customerEnvId, queryClient],
  );

  const readyNotifications = useMemo(() => {
    if (!downloadResourcesQuery?.data?.length) return [];

    return downloadResourcesQuery?.data.filter(notification => notification.status === 'ready');
  }, [downloadResourcesQuery?.data]);

  const unReadNotificationCount = useMemo(() => {
    if (!readyNotifications || !readyNotifications?.length) return 0;

    return readyNotifications.filter(notification => !notification.seen).length;
  }, [readyNotifications]);

  const isAllNotificationsSeen = useMemo(
    () => readyNotifications.every(notification => notification.seen),
    [readyNotifications],
  );

  const onMenuOpen = useCallback(
    event => {
      if (!readyNotifications.length) return;
      setUnreadNotification(true);
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl, readyNotifications, setUnreadNotification],
  );
  const onClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const onClickNotificationDownload = useCallback(
    ({ downloadId, s3Key, event }) => {
      event.stopPropagation();
      if (!s3Key || !downloadId) return;

      onClose();
      updateResourceStatus({ downloadId, resources: readyNotifications });
      DownloadsService.getDownloadFile({ downloadId, s3Key }).then(url => {
        handleUrlDownload(url);
      });
    },
    [onClose, readyNotifications, updateResourceStatus],
  );

  const onClickNotification = useCallback(
    ({ downloadId }) => {
      onClose();
      updateResourceStatus({ downloadId, resources: readyNotifications });
    },
    [onClose, readyNotifications, updateResourceStatus],
  );

  return (
    <>
      <IconButton onClick={onMenuOpen}>
        <Badge
          badgeContent={unReadNotificationCount}
          color="error"
          invisible={!unReadNotificationCount || unreadNotification}>
          <Iconify
            icon="bxs:bell"
            color={!!anchorEl ? colors.green : '#B5B5B5'}
            width={24}
            height={24}
          />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        onClose={onClose}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        styles={{
          maxWidth: 360,
          boxShadow:
            '0px 4px 6px 0px rgba(100, 116, 139, 0.12), 0px 2px 4px 0px rgba(31, 41, 55, 0.06)',
          marginTop: 1,
        }}>
        <MenuItem
          hasActive={false}
          hasHover={false}
          styles={{
            borderBottom: '1px solid #5a5959',
          }}
          disabled={isAllNotificationsSeen}>
          <Stack direction="row" justifyContent="space-between" gap={2} sx={{ width: '100%' }}>
            <ListItemText
              primary="Notifications"
              primaryStyles={{
                fontSize: 14,
                lineHeight: '30px',
                fontWeight: 400,
              }}
            />
            <Stack direction="row" alignItems="center">
              <Link
                onClick={() => updateResourcesStatus({ seen: true })}
                sx={{
                  fontSize: 12,
                }}>
                Mark all as read
              </Link>
            </Stack>
          </Stack>
        </MenuItem>
        {!!readyNotifications?.length &&
          readyNotifications.map(({ id, fileName, seen, s3Key }) => (
            <MenuItem
              key={id}
              color={colors.red}
              onClick={() => onClickNotification({ downloadId: id })}>
              <Stack direction="row" justifyContent="space-between" gap={2} sx={{ width: '100%' }}>
                <ListItemText
                  primary={fileName}
                  primaryStyles={{
                    fontSize: 14,
                    lineHeight: '30px',
                    fontWeight: 400,
                    color: seen ? '#B5B5B5' : '#fff',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                />
                <Stack direction="row" alignItems="center">
                  <Link
                    onClick={event => onClickNotificationDownload({ downloadId: id, s3Key, event })}
                    sx={{
                      fontSize: 12,
                    }}>
                    Click here
                  </Link>
                </Stack>
                {!seen ? (
                  <Stack direction="row" alignItems="center">
                    <Iconify
                      icon="material-symbols:circle"
                      color={colors.green}
                      width={12}
                      height={12}
                    />
                  </Stack>
                ) : (
                  <Box width={12} height={12} />
                )}
              </Stack>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default NotificationBar;
